import { useState } from "react";
import type { Exercise, ReadingQuestion, ReadingAttemptResult } from "../../types";
import { api } from "../../api/client";
import { speakItalian, stopItalian } from "../../lib/tts";
import FeedbackPanel from "./FeedbackPanel";

interface Props {
  exercise: Exercise;
  onDone: (passed: boolean) => void;
  onSkip?: () => void;
}

function SpeakerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

export default function ReadingExercise({ exercise, onDone, onSkip }: Props) {
  const questions = (exercise.options as ReadingQuestion[] | null) ?? [];
  const [phase, setPhase] = useState<"reading" | "answering">("reading");
  const [answers, setAnswers] = useState<(string | null)[]>(() => questions.map(() => null));
  const [grading, setGrading] = useState(false);
  const [result, setResult] = useState<ReadingAttemptResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const allAnswered = answers.every((a) => a !== null);

  const submit = async () => {
    setGrading(true);
    setError(null);
    try {
      setResult(await api.attemptReading(exercise.id, answers as string[]));
    } catch (err) {
      console.error("Reading grading failed:", err);
      setError("Couldn't grade the answers. Please try again.");
    } finally {
      setGrading(false);
    }
  };

  const retry = () => {
    setResult(null);
    setAnswers(questions.map(() => null));
    setError(null);
    setPhase("reading");
  };

  return (
    <div>
      {exercise.prompt && (
        <p className="text-lg text-text-secondary mb-4 leading-relaxed">{exercise.prompt}</p>
      )}

      {phase === "reading" && (
        <>
          <div className="flex items-start gap-3 mb-6">
            <p className="text-[1.05rem] leading-relaxed font-display text-ink flex-1 whitespace-pre-line">
              {exercise.text}
            </p>
            <button
              onClick={() => {
                if (isPlaying) {
                  stopItalian();
                  setIsPlaying(false);
                } else {
                  speakItalian(exercise.text, () => setIsPlaying(false));
                  setIsPlaying(true);
                }
              }}
              aria-label={isPlaying ? "Stop" : "Listen"}
              className="btn-quiet text-xs px-3 py-2 flex-shrink-0"
            >
              <SpeakerIcon /> {isPlaying ? "Stop" : "Listen"}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="btn-primary text-sm px-5"
              onClick={() => {
                stopItalian();
                setIsPlaying(false);
                setPhase("answering");
              }}
            >
              Finished reading
            </button>
            {onSkip && <button onClick={onSkip} className="btn-quiet text-xs px-3 py-1.5">Skip</button>}
          </div>
        </>
      )}

      {phase === "answering" && !result && (
        <div>
          <p className="text-[0.7rem] uppercase tracking-[0.12em] text-text-tertiary font-semibold mb-4">
            Answer about what you read
          </p>
          <div className="space-y-5">
            {questions.map((q, qi) => (
              <div key={qi}>
                <p className="text-[0.95rem] text-ink font-medium mb-2">{q.question}</p>
                <div className="flex flex-col gap-2">
                  {q.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() =>
                        setAnswers((prev) => prev.map((a, i) => (i === qi ? opt : a)))
                      }
                      className={`text-left px-4 py-2.5 rounded-[10px] border text-sm transition-colors ${
                        answers[qi] === opt
                          ? "border-ink bg-surface2 text-ink font-medium"
                          : "border-border hover:border-text-tertiary hover:bg-surface2"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {error && <p className="text-error text-xs mt-3">{error}</p>}
          <button
            className="btn-primary text-sm px-5 mt-5"
            disabled={!allAnswered || grading}
            onClick={submit}
          >
            {grading ? "Grading…" : "Submit answers"}
          </button>
        </div>
      )}

      {result && (
        <FeedbackPanel
          correct={result.passed}
          title={`${result.passed ? "Passed" : "Keep practicing"} · ${result.comprehension_correct}/${result.comprehension_total} correct`}
          onNext={() => onDone(result.passed)}
          onRetry={retry}
        >
          <div className="space-y-2">
            {questions.map((q, qi) => {
              const ok = result.per_question[qi];
              return (
                <div key={qi} className="text-sm">
                  <span className={ok ? "text-success" : "text-error"}>{ok ? "✓" : "✗"}</span>{" "}
                  <span className="text-text-secondary">{q.question}</span>
                  {!ok && (
                    <div className="text-text-tertiary text-xs mt-0.5 ml-4">
                      Correct: <span className="text-text-secondary">{result.correct_answers[qi]}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </FeedbackPanel>
      )}
    </div>
  );
}
